<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import PromptHistoryViewer from 'dashboard/components-next/arrowsAi/PromptHistoryViewer.vue';

const props = defineProps({
  entries: {
    type: Array,
    default: () => [],
  },
  formatContent: {
    type: Function,
    default: value => value,
  },
});

const emit = defineEmits(['restore']);
const { t } = useI18n();
const expandedEntry = ref(null);
const previewLength = 360;
const previewLines = 6;

const restoreText = entry => entry.content || entry.added.join('\n');
const visibleText = entry => props.formatContent(restoreText(entry) || '');
const isLong = entry => {
  const text = visibleText(entry);
  return text.length > previewLength || text.split('\n').length > previewLines;
};

const restore = entry => emit('restore', restoreText(entry));
const restoreAndClose = () => {
  restore(expandedEntry.value);
  expandedEntry.value = null;
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="!entries.length"
      class="rounded-xl border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
    >
      {{ t('ARROWS_AI.HISTORY.EMPTY') }}
    </div>
    <article
      v-for="entry in entries"
      :key="entry.id"
      class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <div class="flex items-center justify-between gap-4 text-sm">
        <span class="font-medium text-n-slate-12">{{ entry.userName }}</span>
        <div class="flex items-center gap-2">
          <time class="text-n-slate-11">
            {{ new Date(entry.createdAt).toLocaleString() }}
          </time>
          <Button
            v-if="restoreText(entry)"
            :label="t('ARROWS_AI.HISTORY.RESTORE')"
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-undo-2"
            @click="restore(entry)"
          />
        </div>
      </div>
      <p
        v-if="visibleText(entry)"
        class="mt-3 line-clamp-6 whitespace-pre-wrap text-sm text-n-slate-12"
        :class="{ 'cursor-pointer': isLong(entry) }"
        @click="isLong(entry) && (expandedEntry = entry)"
      >
        {{ visibleText(entry) }}
      </p>
      <Button
        v-if="isLong(entry)"
        class="mt-2"
        :label="t('ARROWS_AI.HISTORY.SHOW_MORE')"
        variant="link"
        color="slate"
        size="xs"
        @click="expandedEntry = entry"
      />
    </article>
    <PromptHistoryViewer
      v-if="expandedEntry"
      :entry="expandedEntry"
      :text="visibleText(expandedEntry)"
      @close="expandedEntry = null"
      @restore="restoreAndClose"
    />
  </div>
</template>
